import React, { useEffect, useState } from "react";
import PageHeader from "../components/PageHeader";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Spinner } from "react-bootstrap";
import moment from "moment";
import eye from "../assets/eye.svg";
import CommonModal from "../components/CommonModal";
import Paginate from "../components/pagination";
import {
  fetchFormDetailList,
  fetchFormData,
  exportFormCSV
} from "../action/formaction";
import { useToast } from "../components/ToastProvider";

export default function FormList() {
  const { type } = useParams(); // ← Dynamic form type from URL
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [forms, setForms] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [keys, setKeys] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
  });

  const [showModal, setShowModal] = useState(false);
  const [selectedData, setSelectedData] = useState(null);
  const [exportLoading, setExportLoading] = useState(false);
  const { showToast } = useToast();

  const handleExportCSV = async () => {
    setExportLoading(true);

    const res = await exportFormCSV(type);

    if (!res || res.status !== 200) {
      showToast("Failed to export CSV", "error");
      setExportLoading(false);
      return;
    }

    try {
      // Create file download
      const blob = new Blob([res.data], { type: "text/csv" });
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = `${type}.csv`;
      link.click();

      showToast("CSV exported successfully", "success");
    } catch (error) {
      showToast("Failed to export CSV", "error");
    }

    setExportLoading(false);
  };


  // Fetch List
  const loadList = async () => {
    setLoading(true);
    const res = await fetchFormDetailList(
      pagination.page,
      pagination.limit,
      type,
      "",
      "createdAt",
      "desc"
    );

    if (res?.status === 1) {
      setForms(res.data.list);
      setHeaders(res.data.header || []);
      setKeys(res.data.keys || []);
      setPagination((prev) => ({
        ...prev,
        total: Math.ceil(res.data.total / prev.limit),
      }));
    }

    setLoading(false);
  };

  useEffect(() => {
    loadList();
  }, [type, pagination.page, pagination.limit]);

  // Pagination
  const handlePageChange = (page) => {
    const params = new URLSearchParams(location.search);
    params.set("page", page);
    params.set("limit", pagination.limit);

    navigate(`?${params.toString()}`, { replace: true });
    setPagination((p) => ({ ...p, page }));
  };

  const handlePageSizeChange = (limit) => {
    const params = new URLSearchParams(location.search);
    params.set("page", 1);
    params.set("limit", limit);

    navigate(`?${params.toString()}`, { replace: true });
    setPagination((p) => ({ ...p, limit, page: 1 }));
  };

  // Open Popup With Details
  const openDetail = async (id) => {
    const res = await fetchFormData(id);
    if (res?.status === 1) {
      setSelectedData(res.data);
      setShowModal(true);
    }
  };
  const formatValue = (val) => {
    if (!val) return "-";
    if (Array.isArray(val)) return val.join(", ");
    if (moment(val, moment.ISO_8601, true).isValid()) {
      return moment(val).format("YYYY/MM/DD");
    }
    return val;
  };

  return (
    <>
      <PageHeader pageTitle={`${type.replace("-", " ").toUpperCase()}`}>

        <button className="btn-white" onClick={() => navigate("/all-forms")}>
          Back
        </button>
        <button className="btn-black" onClick={handleExportCSV}>
          {exportLoading ? "Exporting..." : "Export CSV"}
        </button>

      </PageHeader>

      <div className="page-container">
        <div className="theme-card p-4">
          <div className="card-title pb-3">All Submissions</div>

          <div className="table-responsive">
            <table className="pages-table">
              <thead>
                <tr>
                  {headers.map((h, i) => (
                    <th key={i}>{h}</th>
                  ))}
                  <th>Date Submitted</th>
                  <th style={{ textAlign: "center" }}>Actions</th>
                </tr>
              </thead>

              <tbody>
                {loading && (
                  <tr>
                    <td colSpan="6" style={{ textAlign: "center" }}>
                      <Spinner size="md" />
                    </td>
                  </tr>
                )}

                {!loading && forms.length === 0 && (
                  <tr>
                    <td colSpan="8" style={{ textAlign: "center" }}>
                      No submissions found.
                    </td>
                  </tr>
                )}

                {!loading &&
                  forms.map((item) => (
                    <tr key={item._id}>
                      {keys.map((k, idx) => (
                        <td key={idx}>{formatValue(item.data?.[k])}</td>
                      ))}
                      <td>
                        {moment(item.createdAt).format("YYYY/MM/DD [at] hh:mm a")}
                      </td>

                      <td style={{ textAlign: "center" }}>
                        <button onClick={() => openDetail(item._id)}>
                          <img src={eye} alt="view" />
                        </button>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>

            <div className="pt-3">
              {Boolean(pagination.total) && (
                <Paginate
                  currentPage={pagination.page}
                  totalPages={pagination.total}
                  onPageChange={handlePageChange}
                  pageSize={pagination.limit}
                  onPageSizeChange={handlePageSizeChange}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ----------- DETAILS POPUP ----------- */}
      <CommonModal show={showModal} onClose={() => setShowModal(false)}>
        <div className="modal-card-body p-1">
          <div className="modal-card-content">
            <div className="card-title mb-3">Complete Details</div>

            {selectedData &&
              Object.entries(selectedData.data || {}).map(([key, val]) => (
                <div key={key}>
                  <div className="contact-name">{key.replace("_", " ")}</div>
                  <div className="contact-details">{String(val) || "-"}</div>
                </div>
              ))}
          </div>

          <div className="card-footer-box">
            <button className="btn-black ms-auto" onClick={() => setShowModal(false)}>
              Okay
            </button>
          </div>
        </div>
      </CommonModal>
    </>
  );
}
